import React from "react";
import { FaPlus, FaThumbtack } from "react-icons/fa";

import { NOTE_STYLES, NOTE_FONT } from "./canvas/constants";

/**
 * Canva-style asset panel: every note rendered as a draggable thumbnail.
 *
 * Drag a card onto the canvas to place it, or click it to drop one in the
 * middle — click matters because HTML5 drag-and-drop doesn't fire on touch
 * devices, so dragging alone would strand phone and tablet users.
 */

function NoteCard({ note, editable, onPlace }) {
  const style = NOTE_STYLES[note.color] || NOTE_STYLES[0];

  return (
    <button
      type="button"
      draggable={editable}
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-sarvavya-note", note._id);
        e.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => editable && onPlace(note._id)}
      disabled={!editable}
      title={editable ? "Drag onto the canvas, or click to place it" : note.text}
      className="note-card"
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        aspectRatio: "1 / 1.08",
        padding: "16px 10px 10px",
        border: "none",
        borderRadius: "8px",
        textAlign: "left",
        background: style.paper,
        cursor: editable ? "grab" : "default",
        boxShadow: note.pinned
          ? "0 0 0 2px #ff6b35, 0 3px 10px rgba(15,23,42,0.2)"
          : "0 3px 10px rgba(15,23,42,0.2)",
        overflow: "hidden",
      }}
    >
      {/* Magnet pin, echoing the corkboard */}
      <span
        style={{
          position: "absolute",
          top: "5px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "13px",
          height: "13px",
          borderRadius: "50%",
          background: `radial-gradient(circle at 33% 28%, rgba(255,255,255,0.95), ${style.pin} 72%)`,
          boxShadow: "0 2px 3px rgba(0,0,0,0.35)",
        }}
      />

      {note.pinned && (
        <FaThumbtack
          style={{
            position: "absolute",
            top: "6px",
            right: "6px",
            fontSize: "0.6rem",
            color: "#ff6b35",
          }}
        />
      )}

      <div
        style={{
          fontFamily: NOTE_FONT,
          fontSize: "0.72rem",
          lineHeight: 1.4,
          color: note.text ? "#1f2937" : "#00000055",
          fontStyle: note.text ? "normal" : "italic",
          // Clamp to a thumbnail's worth of text rather than overflowing.
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {note.text || "Empty note"}
      </div>

      {(note.tags || []).length > 0 && (
        <div
          style={{
            position: "absolute",
            left: "8px",
            right: "8px",
            bottom: "7px",
            display: "flex",
            gap: "3px",
            overflow: "hidden",
          }}
        >
          {note.tags.slice(0, 2).map((t) => (
            <span
              key={t}
              style={{
                background: "rgba(0,0,0,0.09)",
                borderRadius: "8px",
                padding: "1px 6px",
                fontSize: "0.55rem",
                color: "#334155",
                whiteSpace: "nowrap",
              }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {editable && (
        <span className="note-card__add" aria-hidden="true">
          <FaPlus />
        </span>
      )}
    </button>
  );
}

function Grid({ notes, editable, onPlace }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(112px, 1fr))",
        gap: "10px",
      }}
    >
      {notes.map((note) => (
        <NoteCard key={note._id} note={note} editable={editable} onPlace={onPlace} />
      ))}
    </div>
  );
}

function SectionLabel({ children, count }) {
  return (
    <div
      className="fw-bold"
      style={{
        fontSize: "0.68rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#94a3b8",
        margin: "16px 0 8px",
      }}
    >
      {children}
      {count != null && <span style={{ fontWeight: 400 }}> · {count}</span>}
    </div>
  );
}

function NotesLibrary({ board, editable, dark, onPlace }) {
  const { visible, notes, loading } = board;

  const pinned = visible.filter((n) => n.pinned);
  const rest = visible.filter((n) => !n.pinned);

  const headerBorder = dark ? "1px solid #1f2f49" : "1px solid #e2e8f0";
  const text = dark ? "#e2e8f0" : "#334155";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Hover/press affordances need real CSS — inline styles can't do :hover */}
      <style>{`
        .note-card { transition: transform .12s ease, box-shadow .12s ease; }
        .note-card:not(:disabled):hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 18px rgba(15,23,42,0.28) !important;
        }
        .note-card:not(:disabled):active { cursor: grabbing; transform: translateY(-1px); }
        .note-card__add {
          position: absolute; right: 6px; bottom: 6px;
          width: 22px; height: 22px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: #ff6b35; color: #0f172a; font-size: .6rem;
          opacity: 0; transition: opacity .12s ease;
        }
        .note-card:hover .note-card__add { opacity: 1; }
      `}</style>

      <div
        className="d-flex align-items-center justify-content-between"
        style={{ padding: "11px 14px", borderBottom: headerBorder, color: text, flexShrink: 0 }}
      >
        <span className="fw-bold" style={{ fontSize: "0.82rem" }}>
          All notes
          <span style={{ color: "#94a3b8", fontWeight: 400 }}> · {visible.length}</span>
        </span>
        {editable && (
          <span style={{ fontSize: "0.66rem", color: "#94a3b8" }}>drag onto canvas →</span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "4px 14px 16px" }}>
        {loading && (
          <p style={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.85rem", marginTop: 14 }}>
            Loading notes…
          </p>
        )}

        {!loading && visible.length === 0 && (
          <p style={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.85rem", marginTop: 14 }}>
            {notes.length === 0
              ? "No notes yet — add one from the toolbar above."
              : "No notes match your search."}
          </p>
        )}

        {pinned.length > 0 && (
          <>
            <SectionLabel count={pinned.length}>Pinned</SectionLabel>
            <Grid notes={pinned} editable={editable} onPlace={onPlace} />
          </>
        )}

        {rest.length > 0 && (
          <>
            {pinned.length > 0 && <SectionLabel count={rest.length}>Everything else</SectionLabel>}
            <Grid notes={rest} editable={editable} onPlace={onPlace} />
          </>
        )}
      </div>
    </div>
  );
}

export default NotesLibrary;
